import { ReactNode, useCallback, useState } from "react";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { ScheduleMarkBilledModal } from "./ScheduleMarkBilledModal";
import { ScheduleMarkUnbilledModal } from "./ScheduleMarkUnbilledModal";
import { ScheduleBillingTarget } from "./scheduleBillingRowHelpers";

export enum ScheduleBillingAction {
    markBilled = "mark_billed",
    markUnbilled = "mark_unbilled",
}

export interface ScheduleBillingRequest {
    action: ScheduleBillingAction;
    target: ScheduleBillingTarget;
}

interface UseScheduleBillingActionsResult {
    requestBillingAction: (request: ScheduleBillingRequest) => void;
    /** Mount once, outside any popover or table cell. */
    modals: ReactNode;
}

/**
 * Hosts the "Mark as billed" and "Mark as unbilled" modals. A View-only user
 * is refused on the click, before a modal opens that could only fail.
 */
export function useScheduleBillingActions(): UseScheduleBillingActionsResult {
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const [request, setRequest] = useState<ScheduleBillingRequest | null>(null);

    const requestBillingAction = useCallback(
        (nextRequest: ScheduleBillingRequest) => {
            if (refuseIfViewOnly()) {
                return;
            }
            setRequest(nextRequest);
        },
        [refuseIfViewOnly],
    );
    const close = useCallback(() => setRequest(null), []);

    const modals = (
        <>
            <ScheduleMarkBilledModal
                target={
                    request?.action === ScheduleBillingAction.markBilled
                        ? request.target
                        : null
                }
                onHide={close}
            />
            <ScheduleMarkUnbilledModal
                target={
                    request?.action === ScheduleBillingAction.markUnbilled
                        ? request.target
                        : null
                }
                onHide={close}
            />
        </>
    );

    return { requestBillingAction, modals };
}
